import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { languages, tr, type LanguageCode, type TranslationKey } from "./translations";

const STORAGE_KEY = "cp.language";

type I18nState = {
  language: LanguageCode;
  setLanguage: (code: LanguageCode) => void;
  t: (key: TranslationKey) => string;
};

const I18nContext = createContext<I18nState | null>(null);

function detect(): LanguageCode {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && stored in languages) return stored as LanguageCode;
  const browser = navigator.language.slice(0, 2);
  return browser in languages ? (browser as LanguageCode) : "tr";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>(detect);

  const setLanguage = useCallback((code: LanguageCode) => {
    localStorage.setItem(STORAGE_KEY, code);
    setLanguageState(code);
  }, []);

  const value = useMemo<I18nState>(() => {
    const dictionary = languages[language];
    return {
      language,
      setLanguage,
      // Falls back to Turkish, which is the only dictionary guaranteed
      // complete by the type system.
      t: (key) => dictionary[key] ?? tr[key],
    };
  }, [language, setLanguage]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nState {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used inside I18nProvider");
  return context;
}
